import React, { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import api, { fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

// entity -> { title, subtitle, fields:[{k,label,type,options}], columns:[k], import? }
const CONFIG = {
  "holidays": { title: "Holidays", subtitle: "Regular and special holidays by location", fields: [{ k: "name", label: "Name" }, { k: "date", label: "Date", type: "date" }, { k: "type", label: "Type", type: "select", options: ["Regular", "Special"] }, { k: "location", label: "Location" }], columns: ["name", "date", "type", "location"] },
  "leave-types": { title: "Leave Types", subtitle: "Vacation & Sick are system-locked", fields: [{ k: "name", label: "Name" }, { k: "code", label: "Code" }, { k: "active", label: "Active", type: "select", options: ["true", "false"] }], columns: ["name", "code", "locked", "active"] },
  "departments": { title: "Departments", subtitle: "Organizational departments", fields: [{ k: "name", label: "Name" }, { k: "code", label: "Code" }], columns: ["name", "code"] },
  "employee-levels": { title: "Employee Level", subtitle: "Levels available in the Employee Directory 'Level' selector. Each level's attendance rule (grace/lateness with Overtime & Night Differential, 10.5 hrs per day, or 96 hrs per cut-off) is set in Config › Work Schedules › Roles & Permission.", fields: [{ k: "name", label: "Level" }], columns: ["name"] },
  "cost-center": { title: "Cost Center", subtitle: "Cost centers", fields: [{ k: "code", label: "Code" }, { k: "name", label: "Name" }], columns: ["code", "name"] },
  "projects": { title: "Projects", subtitle: "Project sites", fields: [{ k: "code", label: "Code" }, { k: "name", label: "Name" }, { k: "location", label: "Location" }], columns: ["code", "name", "location"] },
  "early-cutoff": { title: "Early Cutoff", subtitle: "Cut-off date ranges", fields: [{ k: "name", label: "Name" }, { k: "start_day", label: "Start Day", type: "number" }, { k: "end_day", label: "End Day", type: "number" }], columns: ["name", "start_day", "end_day"] },
};

export default function Masterfile({ entity }) {
  const cfg = CONFIG[entity];
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = useCallback(() => api.get(`/hris/mf/${entity}`).then(({ data }) => setRows(data)).catch(() => {}), [entity]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({}); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...r }); setOpen(true); };
  const save = async () => {
    try {
      const body = { ...form };
      if ("active" in body) body.active = String(body.active) === "true";
      if (editing) await api.put(`/hris/mf/${entity}/${editing.id}`, body);
      else await api.post(`/hris/mf/${entity}`, body);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const del = async (r) => { if (!window.confirm("Delete this record?")) return; try { await api.delete(`/hris/mf/${entity}/${r.id}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); } };

  const cell = (r, k) => (k === "locked" ? (r.locked ? <Badge className="bg-slate-200 text-slate-600 text-[10px]">Locked</Badge> : "—") : (k === "active" ? (r.active === false ? "No" : "Yes") : (r.date || r[k] ? (k === "date" ? fmtDate(r[k]) : String(r[k] ?? "")) : "—")));

  return (
    <div className="uemp-fade-in">
      <PageHeader title={cfg.title} subtitle={cfg.subtitle} actions={
        <div className="flex gap-2">
          <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700" data-testid="mf-new-btn"><Icons.Plus className="h-4 w-4 mr-1.5" /> New</Button>
        </div>
      } />
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">{cfg.columns.map((c) => <TableHead key={c} className="capitalize">{c.replace(/_/g, " ")}</TableHead>)}<TableHead className="w-24">Actions</TableHead></TableRow></TableHeader>
          <TableBody data-testid="mf-table">
            {rows.length === 0 && <TableRow><TableCell colSpan={cfg.columns.length + 1} className="text-center text-slate-400 py-8">No records.</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.id} data-testid="mf-row">
                {cfg.columns.map((c) => <TableCell key={c} className="text-sm">{cell(r, c)}</TableCell>)}
                <TableCell>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(r)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid="mf-edit"><Icons.Pencil className="h-4 w-4" /></button>
                    {!(entity === "leave-types" && r.locked) && <button onClick={() => del(r)} className="p-1.5 rounded hover:bg-rose-50 text-rose-500" data-testid="mf-delete"><Icons.Trash2 className="h-4 w-4" /></button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} {cfg.title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {cfg.fields.map((f) => {
              const locked = entity === "leave-types" && editing?.locked && f.k !== "active";
              return (
                <div key={f.k} className="space-y-2">
                  <Label>{f.label}{locked && <span className="text-[10px] text-slate-400"> (locked)</span>}</Label>
                  {f.type === "select"
                    ? <select className="w-full border rounded-lg h-10 px-3 text-sm" value={form[f.k] ?? f.options[0]} disabled={locked} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} data-testid={`mf-field-${f.k}`}>{f.options.map((o) => <option key={o} value={o}>{o}</option>)}</select>
                    : <Input type={f.type || "text"} value={form[f.k] ?? ""} disabled={locked} onChange={(e) => setForm({ ...form, [f.k]: f.type === "number" ? Number(e.target.value) : e.target.value })} data-testid={`mf-field-${f.k}`} />}
                </div>
              );
            })}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="mf-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
